import { DateTimeService, DateTimeUtils } from '@mews/framework';
import { CalendarService } from '@optimus-web/core';
import { IntervalAdjustmentFormKeys, IntervalAdjustmentFormValues } from './schema';

export const EMPTY_RESPONSE = {
    newNightsCreated: false,
    someNightsCanceled: false,
    onlyOneSideOfIntervalWasExtended: false,
};

export const compareFormValues = (
    currentValues: IntervalAdjustmentFormValues,
    { startUtc, endUtc }: { startUtc: string; endUtc: string },
    timezone: string,
    calendarService: CalendarService
) => {
    const arrival = currentValues[IntervalAdjustmentFormKeys.Arrival];
    const departure = currentValues[IntervalAdjustmentFormKeys.Departure];

    if (!arrival || !departure) {
        return EMPTY_RESPONSE;
    }

    const isNewArrivalBeforeOldOne = arrival < startUtc;
    const isNewDepartureBeforeOldOne = departure < endUtc;

    const startStruc = DateTimeUtils.utcToStructuredDate(startUtc, timezone);
    const arrivalStruc = DateTimeUtils.utcToStructuredDate(arrival, timezone);
    const endStruc = DateTimeUtils.utcToStructuredDate(endUtc, timezone);
    const departureStruc = DateTimeUtils.utcToStructuredDate(departure, timezone);

    const arrivalDiffNights =
        calendarService.getDiffNights(arrivalStruc, startStruc) * (isNewArrivalBeforeOldOne ? -1 : 1);
    const departureDiffNights =
        calendarService.getDiffNights(departureStruc, endStruc) * (isNewDepartureBeforeOldOne ? -1 : 1);

    const newNightsCreated = arrivalDiffNights < 0 || departureDiffNights > 0;

    return {
        newNightsCreated,
        someNightsCanceled: arrivalDiffNights > 0 || departureDiffNights < 0,

        onlyOneSideOfIntervalWasExtended: newNightsCreated && (departureDiffNights === 0 || arrivalDiffNights === 0),
    };
};

export const compareFormValuesForMultipleIntervals = (
    currentValues: IntervalAdjustmentFormValues,
    intervals: { startUtc: string; endUtc: string }[],
    timezone: string,
    calendarService: CalendarService
) =>
    intervals.reduce((acc, interval) => {
        const { newNightsCreated, someNightsCanceled, onlyOneSideOfIntervalWasExtended } = compareFormValues(
            currentValues,
            interval,
            timezone,
            calendarService
        );
        return {
            newNightsCreated: acc.newNightsCreated || newNightsCreated,
            someNightsCanceled: acc.someNightsCanceled || someNightsCanceled,
            onlyOneSideOfIntervalWasExtended: acc.onlyOneSideOfIntervalWasExtended || onlyOneSideOfIntervalWasExtended,
        };
    }, EMPTY_RESPONSE);

export const getUtcWithOriginalSecondsAndMilliseconds = (utc: string, originalUtc: string, timezone: string) => {
    const originalTime = DateTimeService.fromUtcString(originalUtc, timezone);
    let newTime = DateTimeService.fromUtcString(utc, timezone);

    // set seconds and milliseconds to 0
    newTime = DateTimeService.subtractSeconds(newTime, DateTimeService.getSeconds(newTime));
    newTime = DateTimeService.subtractMilliseconds(newTime, DateTimeService.getMilliseconds(newTime));
    // set seconds and milliseconds from the originalUtc
    newTime = DateTimeService.addSeconds(newTime, DateTimeService.getSeconds(originalTime));
    newTime = DateTimeService.addMilliseconds(newTime, DateTimeService.getMilliseconds(originalTime));

    return DateTimeService.toUtc(newTime).value;
};
